import { useMutation } from '@tanstack/react-query';
import { Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { getFeaturePermission, RouterLinks } from '@zeta-commons';
import {
  GlobalUploadBar,
  useInvoiceDetailStore,
  useMPSConfigStore,
  useSubscriptionDetailsStore,
  useUploadStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import { ClipboardCheckIcon, PhonePausedIcon, ReportsIcon, UserCircleSvgIcon } from '@zeta-core/assets';
import { MpsService } from '@zeta-services';
import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { UserPathAccessRoles } from '../../auth/route-constants';
import { Main } from '../common/main';
import Header from './header';
import { HelpCircleIcon } from 'lucide-react';
import FloatingSupport from '../../components/floating-support';
import { useFlexiblePlanAccess } from '../../hooks/use-flexible-plan-access';

interface IDashboardLayoutProps {
  children: ReactNode;
  noData?: boolean;
  hideNavOptions?: boolean;
}

export const DashboardLayout = ({ noData, children }: IDashboardLayoutProps) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const navigate = useNavigate();
  const location = useLocation();
  const uploads = useUploadStore((state) => state.uploads);
  const isUploadInProgress = uploads.some((u) => u.status === 'uploading');
  const [showReloadConfirm, setShowReloadConfirm] = useState(false);

  // In-app navigation is allowed; GlobalUploadBar is app-level so uploads continue. Only prompt on reload (F5).

  // Intercept reload keys (F5, Ctrl+R, Cmd+R) in capture phase so Zeta dialog shows instead of native
  useEffect(() => {
    if (!isUploadInProgress) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const isReloadKey = e.key === 'F5' || (e.key === 'r' && (e.ctrlKey || e.metaKey));
      if (isReloadKey) {
        e.preventDefault();
        e.stopPropagation();
        setShowReloadConfirm(true);
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isUploadInProgress]);

  const currentSubscription = useSubscriptionDetailsStore((state) => state.subscription);
  const subscriptionSuspended = useInvoiceDetailStore((state) => state.suspended);
  const mpsConfigStore = useMPSConfigStore((state) => state);
  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  const isClientAccessible = userAccess?.access ? Object.keys(userAccess.access).some((featureName) => featureName.includes('Advisor-Client')) : false;
  const mpsPermission = getFeaturePermission(userAccess?.access, 'AdvisorMPS');
  const isMPSAccessible = mpsPermission.accessible;
  const { mutate: mutateBenchMarkData } = useMutation({
    mutationFn: () =>
      MpsService()
        .getMPSConfiguration(accessibleEntityID)
        .then((response) => mpsConfigStore.updateMPSConfigDetails(response.data.MPSConfig, true)), // Extract MPSConfig from response
  });

  useEffect(() => {
    if (isMPSAccessible && currentSubscription) {
      mutateBenchMarkData();
    }
  }, [mutateBenchMarkData, isMPSAccessible, currentSubscription, accessibleEntityID]);

  const DASHBOARD_HEADER = [
    /*{
      title: 'Dashboard',
      id: 'dashboard-tab',
      path: RouterLinks.dashboardLink,
      icon: ClipboardCheckIcon,
      show: getFeaturePermission(userAccess?.access, 'AdvisorDashboard').accessible,
    },*/
    {
      title: 'Copilot',
      id: 'copilot-tab',
      path: RouterLinks.advisorCopilot,
      icon: PhonePausedIcon,
      show: getFeaturePermission(userAccess?.access, 'Advisor-Client-AI').accessible || isClientAccessible,
    },
    { title: 'Clients', id: 'clients-tab', path: RouterLinks.clients, icon: UserCircleSvgIcon, show: isClientAccessible },
    {
      title: 'My Task',
      id: 'tasks-tab',
      path: RouterLinks.myTasks,
      icon: ClipboardCheckIcon,
      show: getFeaturePermission(userAccess?.access, 'AdvisorTaskWorkflow').accessible,
    },
    {
      title: 'Manage Portfolios',
      id: 'manage-portfolio-tab',
      path: RouterLinks.managePortfolio,
      icon: ReportsIcon,
      show: mpsPermission.hasWritePermission,
    },
    {
      title: 'Meetings',
      id: 'meetings-tab',
      path: RouterLinks.meetings,
      icon: PhonePausedIcon,
      show: getFeaturePermission(userAccess?.access, 'AdvisorMeeting').accessible,
    },
    {
      title: 'Support',
      id: 'support-tab',
      path: RouterLinks.support,
      icon: HelpCircleIcon,
      show: true,
    },
  ];

  const { role } = useUserDetailsStore((state) => state.user);
  const { hasFlexiblePlanAccess } = useFlexiblePlanAccess();

  const hideOptions = () => {
    if (
      !hasFlexiblePlanAccess ||
      subscriptionSuspended ||
      currentSubscription?.initialPaymentPending
      // progressData?.firmProfileSetupComplete === false
    ) {
      return true;
    }
    return false;
  };

  const isPaymentPending = currentSubscription?.initialPaymentPending;
  useEffect(() => {
    const isOnBillingPage = location.pathname.includes(RouterLinks.billingAndInvoice);
    const isOnPaymentPage = location.pathname.includes(RouterLinks.completePayment);
    const isOnPricingPage = location.pathname.includes(RouterLinks.pricing);

    // Only handle payment pending and suspended subscriptions here
    // No subscription cases are now handled by ProtectedRoute and HomePage
    if ((role && role === UserPathAccessRoles.ADMIN) || role === UserPathAccessRoles.SUPER_ADMIN) {
      if (isPaymentPending && !isOnBillingPage && !isOnPaymentPage && !isOnPricingPage) {
        navigate(RouterLinks.billingAndInvoice, { state: { fromPath: 'invoices' } });
      }
      if (subscriptionSuspended && !isOnBillingPage && !isOnPaymentPage && !isOnPricingPage) {
        navigate(RouterLinks.billingAndInvoice, { state: { fromPath: 'invoices' } });
      }
    }
  }, [currentSubscription, subscriptionSuspended, location.pathname, navigate, role]);

  return (
    <>
      {/* <Header navData={DASHBOARD_HEADER} noData={noData} hideNavOptions={subscriptionSuspended || noSubscription || hideNavOptions} /> */}
      <Header navData={DASHBOARD_HEADER} noData={noData} hideNavOptions={hideOptions()} />
      <Main>{children}</Main>
      <GlobalUploadBar />

      {/* Zeta dialog when user presses F5/Ctrl+R with uploads in progress (in-app navigation is not blocked) */}
      <Dialog open={showReloadConfirm} onClose={() => setShowReloadConfirm(false)} aria-labelledby="leave-upload-dialog-title">
        <DialogTitle id="leave-upload-dialog-title">Uploads in progress</DialogTitle>
        <DialogContent>
          <DialogContentText>You have file(s) uploading. If you reload now, uploads may be cancelled. Do you want to reload anyway?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowReloadConfirm(false)} variant="contained" color="primary" autoFocus>
            Stay
          </Button>
          <Button
            onClick={() => {
              setShowReloadConfirm(false);
              window.location.reload();
            }}
            variant="outlined"
            color="inherit"
          >
            Reload anyway
          </Button>
        </DialogActions>
      </Dialog>

      <FloatingSupport />
    </>
  );
};
